import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Timestamp } from 'firebase/firestore';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import esLocale from 'date-fns/locale/es';
import enLocale from 'date-fns/locale/en-US';
import { Company, Reservation } from '~/core/types';
import { useAuth } from '~/hooks/useAuth';
import ReservationSummary from '~/pages/public/Forms/Reservation/components/ReservationSummary';
import { formatDate, formatHourWithPeriod, getTimestampUtcToZonedTime } from '~/utils/date';

/**
 * La tarjeta de confirmación de una reserva, abierta otra vez desde el panel.
 *
 * Es la misma pantalla que ve el cliente (y quien atiende) justo al terminar
 * de reservar: la imagen de NOI con el resumen encima. El equipo le hace una
 * captura de pantalla y se la manda al cliente por WhatsApp; el problema era
 * que, cerrada esa ventana, no había forma de volver a verla. Ahora se abre
 * desde la tabla y desde el calendario con el icono de la cámara.
 *
 * Reutiliza `ReservationSummary` tal cual (la misma tarjeta), no la pantalla
 * de confirmación entera: esa dispara el registro de conversión de Google al
 * montarse, y volver a abrirla contaría conversiones que no existen.
 */

type Props = {
  reserva: Record<string, any> | null | undefined;
  isOpen: boolean;
  onClose: () => void;
};

/** El idioma en que el cliente hizo la reserva; español si no consta. */
const idiomaDe = (reserva: Record<string, any>): 'es' | 'en' =>
  reserva.lang === 'en' ? 'en' : 'es';

/** Para las empresas que no tienen tarjeta propia: el mismo texto de siempre. */
const ResumenSencillo = ({ reserva }: { reserva: Record<string, any> }) => {
  const { i18n } = useTranslation();
  const idioma = idiomaDe(reserva);
  const t = i18n.getFixedT(idioma);
  const inicio = (reserva.startDatetime as Timestamp | undefined)?.toDate?.();
  const fin = (reserva.endDatetime as Timestamp | undefined)?.toDate?.();
  const dia = useMemo(
    () =>
      inicio
        ? formatDate(
            getTimestampUtcToZonedTime(inicio),
            'dd MMMM yyyy',
            idioma == 'es' ? esLocale : enLocale
          )
        : '',
    [inicio, idioma]
  );
  return (
    <VStack py={8} spacing={3}>
      <Text textAlign='center' fontWeight='bold'>
        {t('reserve_confirmation.title_chunk_1')} {reserva.namesAndSurnames ?? reserva.name}{' '}
        {t('reserve_confirmation.title_chunk_2')} #{reserva.code}{' '}
        {t('reserve_confirmation.title_chunk_3')} {dia} {inicio ? formatHourWithPeriod(inicio) : ''}{' '}
        {fin ? `a ${formatHourWithPeriod(fin)}` : ''}
      </Text>
      <Text textAlign='center' color='gray.600'>
        {reserva.location?.name} · {reserva.numberPeople} {t('reserve_confirmation.text_people')}
      </Text>
    </VStack>
  );
};

const TarjetaReserva = ({ reserva, isOpen, onClose }: Props) => {
  const { user } = useAuth();
  const empresa = user?.company as Company | undefined;
  const conTarjeta = empresa?.externalId === 'noi-remb';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='5xl' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent bg='transparent' boxShadow='none' data-testid='tarjeta-confirmacion'>
        <ModalCloseButton
          bg='white'
          borderRadius='full'
          boxShadow='md'
          zIndex={2}
          aria-label='Cerrar la tarjeta'
        />
        <ModalBody p={0}>
          {reserva &&
            (conTarjeta ? (
              <ReservationSummary
                reservation={reserva as unknown as Reservation}
                reservationId={reserva.id}
                company={empresa}
                goBack={onClose}
                soloVer
                idioma={idiomaDe(reserva)}
              />
            ) : (
              <VStack bg='white' borderRadius='2xl' px={6}>
                <ResumenSencillo reserva={reserva} />
              </VStack>
            ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TarjetaReserva;
